/**
 * Shared hook for handling OAuth errors in register and login pages.
 * Extracts common error handling logic to reduce duplication.
 */

import { useEffect } from 'react';
import { useSearchParams } from 'next/navigation';
import { useToast } from '@/hooks/use-toast';
import { removeQueryParams } from '@/lib/utils/url';

/**
 * Hook to handle OAuth errors from URL parameters and hash fragments.
 * Handles both query string errors and Supabase Auth hash fragment errors.
 */
export function useOAuthErrorHandling() {
  const searchParams = useSearchParams();
  const { toast } = useToast();

  // Handle OAuth errors from URL query parameters.
  // Some callback redirects include both `error` and `message`, while others
  // may only include `message` (e.g., "Account not found" redirect). Handle both.
  useEffect(() => {
    const error = searchParams.get('error');
    const message = searchParams.get('message');

    if (error && message) {
      toast({
        title: 'Authentication Error',
        description: decodeURIComponent(message),
        variant: 'destructive',
      });
    } else if (message && !error) {
      // Standalone message without error code (e.g., informational redirect)
      toast({
        title: 'Notice',
        description: decodeURIComponent(message),
        variant: 'default',
      });
    }
  }, [searchParams, toast]);

  // Handle Supabase Auth hash fragment errors (e.g., expired OTP redirects)
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const hash = window.location.hash;
    if (!hash) return;

    // Parse hash fragment for Supabase Auth errors
    const hashParams = new URLSearchParams(hash.substring(1)); // Remove '#'
    const error = hashParams.get('error');
    const errorCode = hashParams.get('error_code');
    const errorDescription = hashParams.get('error_description');

    if (error) {
      // Clear query params from URL
      removeQueryParams('all', window.location.pathname);

      // Show appropriate error message
      if (errorCode === 'otp_expired') {
        toast({
          title: 'Link Expired',
          description: 'The email link has expired. Please request a new one.',
          variant: 'destructive',
        });
      } else if (error === 'access_denied') {
        toast({
          title: 'Access Denied',
          description: errorDescription || 'Authentication was cancelled or denied.',
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Authentication Error',
          description:
            errorDescription || 'An error occurred during authentication. Please try again.',
          variant: 'destructive',
        });
      }
    }
  }, [toast]);
}
